import * as React from 'react';
import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  videoPlayerBarRecipe,
  videoPlayerControlRecipe,
  videoPlayerControlsEndRecipe,
  videoPlayerControlsRecipe,
  videoPlayerFillRecipe,
  videoPlayerMediaRecipe,
  videoPlayerRangeRecipe,
  videoPlayerRecipe,
  videoPlayerSeekRecipe,
  videoPlayerStageRecipe,
  videoPlayerStartGlyphRecipe,
  videoPlayerStartLabelRecipe,
  videoPlayerStartRecipe,
  videoPlayerThumbRecipe,
  videoPlayerTimeRecipe,
  videoPlayerTrackRecipe,
  videoPlayerWatchedBarRecipe,
  videoPlayerWatchedRecipe,
  videoPlayerWatchedWordRecipe,
} from '../recipes/video-player.recipe';

export interface VideoPlayerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  src: string;
  /**
   * A still to show before playing. Without one the player shows the video's
   * own frame at half a second in (a `#t=0.5` media fragment), which phones
   * render where a bare `preload="metadata"` often leaves a black box.
   */
  posterUrl?: string;
  /** What the video is, for a screen reader: "Your video from Relay Tow". */
  label: string;
  /** The words under the big play button. */
  startLabel?: string;
  /**
   * The <video> element, for code that listens to it: the sales page's
   * watch tracking hangs its own timeupdate and play listeners off this.
   */
  videoRef?: React.Ref<HTMLVideoElement>;
}

/** 75 -> "1:15". */
export function formatVideoTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = value;
}

type FullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};

/**
 * A video with its own controls: play and pause, mute, the time, a seek bar,
 * how much has been watched, and full screen.
 *
 * Before the first play a large round play button sits over the picture.
 * Nothing autoplays, and nothing animates: no pulsing button, no loading
 * shimmer. `playsInline` keeps an iPhone from jumping to full screen on play.
 */
export const VideoPlayer = React.forwardRef<HTMLDivElement, VideoPlayerProps>(
  function VideoPlayer(
    {
      src,
      posterUrl,
      label,
      startLabel = 'Watch your video',
      videoRef,
      className,
      ...props
    },
    ref
  ) {
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const mediaRef = React.useRef<HTMLVideoElement | null>(null);
    const [started, setStarted] = React.useState(false);
    const [playing, setPlaying] = React.useState(false);
    const [muted, setMuted] = React.useState(false);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [furthest, setFurthest] = React.useState(0);
    const [fullscreen, setFullscreen] = React.useState(false);

    const setRoot = React.useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        setRef(ref, node);
      },
      [ref]
    );
    const setMedia = React.useCallback(
      (node: HTMLVideoElement | null) => {
        mediaRef.current = node;
        setRef(videoRef, node);
      },
      [videoRef]
    );

    React.useEffect(() => {
      const onChange = () =>
        setFullscreen(
          !!document.fullscreenElement &&
            document.fullscreenElement === rootRef.current
        );
      document.addEventListener('fullscreenchange', onChange);
      return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    // No poster: show the frame half a second in rather than a black box.
    const mediaSrc = posterUrl || src.includes('#') ? src : `${src}#t=0.5`;

    const play = () => {
      const video = mediaRef.current;
      if (!video) return;
      // The first play starts from the top, not from the preview frame.
      if (!started && !posterUrl && video.currentTime <= 0.6) {
        video.currentTime = 0;
      }
      setStarted(true);
      void video.play()?.catch(() => undefined);
    };

    const toggle = () => {
      const video = mediaRef.current;
      if (!video) return;
      if (video.paused || video.ended) play();
      else video.pause();
    };

    const toggleMute = () => {
      const video = mediaRef.current;
      if (video) video.muted = !video.muted;
    };

    const toggleFullscreen = () => {
      const root = rootRef.current;
      const video = mediaRef.current as FullscreenVideo | null;
      if (document.fullscreenElement) {
        void document.exitFullscreen?.()?.catch(() => undefined);
      } else if (root?.requestFullscreen) {
        void root.requestFullscreen().catch(() => undefined);
      } else {
        // iPhone Safari: only the video element itself can go full screen.
        video?.webkitEnterFullscreen?.();
      }
    };

    const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
      const video = mediaRef.current;
      if (!video) return;
      const to = Number(event.target.value);
      video.currentTime = to;
      setCurrent(to);
    };

    const readDuration = (video: HTMLVideoElement) => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
    };

    const position = duration > 0 ? Math.min(current / duration, 1) : 0;
    const watched =
      duration > 0 ? Math.min(Math.round((furthest / duration) * 100), 100) : 0;

    return (
      <div
        ref={setRoot}
        className={cn(videoPlayerRecipe(), className)}
        {...props}
      >
        <div className={videoPlayerStageRecipe()}>
          <video
            ref={setMedia}
            src={mediaSrc}
            poster={posterUrl}
            preload="metadata"
            playsInline
            aria-label={label}
            className={videoPlayerMediaRecipe()}
            onClick={toggle}
            onPlay={() => {
              setStarted(true);
              setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onVolumeChange={(event) => setMuted(event.currentTarget.muted)}
            onLoadedMetadata={(event) => readDuration(event.currentTarget)}
            onDurationChange={(event) => readDuration(event.currentTarget)}
            onTimeUpdate={(event) => {
              const video = event.currentTarget;
              setCurrent(video.currentTime);
              if (!video.paused) {
                setFurthest((max) => Math.max(max, video.currentTime));
              }
            }}
          />
          {!started ? (
            <button
              type="button"
              className={videoPlayerStartRecipe()}
              onClick={play}
            >
              <span
                className={videoPlayerStartGlyphRecipe()}
                aria-hidden="true"
              >
                <Play />
              </span>
              <span className={videoPlayerStartLabelRecipe()}>
                {startLabel}
                {duration > 0 ? ` · ${formatVideoTime(duration)}` : null}
              </span>
            </button>
          ) : null}
        </div>

        <div className={videoPlayerBarRecipe()}>
          <div className={videoPlayerSeekRecipe()}>
            <div className={videoPlayerTrackRecipe()}>
              <div
                className={videoPlayerFillRecipe()}
                style={{ width: `${position * 100}%` }}
                aria-hidden="true"
              />
            </div>
            <div
              className={videoPlayerThumbRecipe()}
              style={{ left: `${position * 100}%` }}
              aria-hidden="true"
            />
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(current, duration || 0)}
              onChange={seek}
              aria-label="Seek video"
              aria-valuetext={`${formatVideoTime(current)} of ${formatVideoTime(duration)}`}
              className={videoPlayerRangeRecipe()}
            />
          </div>

          <div className={videoPlayerControlsRecipe()}>
            <button
              type="button"
              aria-label={playing ? 'Pause' : 'Play'}
              className={videoPlayerControlRecipe()}
              onClick={toggle}
            >
              {playing ? <Pause /> : <Play />}
            </button>
            <button
              type="button"
              aria-label={muted ? 'Unmute' : 'Mute'}
              className={videoPlayerControlRecipe()}
              onClick={toggleMute}
            >
              {muted ? <VolumeX /> : <Volume2 />}
            </button>
            <span className={videoPlayerTimeRecipe()}>
              {formatVideoTime(current)} / {formatVideoTime(duration)}
            </span>
            <div className={videoPlayerControlsEndRecipe()}>
              <span className={videoPlayerWatchedRecipe()}>
                <span
                  className={videoPlayerWatchedBarRecipe()}
                  aria-hidden="true"
                >
                  <span
                    className={videoPlayerFillRecipe()}
                    style={{ width: `${watched}%` }}
                  />
                </span>
                <span>
                  {watched}%
                  <span className={videoPlayerWatchedWordRecipe()}>
                    {' watched'}
                  </span>
                </span>
              </span>
              <button
                type="button"
                aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
                className={videoPlayerControlRecipe()}
                onClick={toggleFullscreen}
              >
                {fullscreen ? <Minimize /> : <Maximize />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
